import Alert from '@storeblocks/alert';
import { Button } from '@storeblocks/button';
import Modal from '@storeblocks/modal';
import { AxiosError } from 'axios';
import { type FC, useEffect, useState } from 'react';

import { useDeleteDocumentFileMutation } from '@/api/documentArchive/documentArchiveApi';
import { useKeyPress } from '@/hooks/useKeyPress';
import { useTexts } from '@/hooks/useTexts';
import { useDocumentsArchiveContext } from '@/pages/Archives/DocumentsArchive/DocumentsArchiveContext';
import { TrackingElement, useGetTrackId } from '@/tracking';

import { WithGap } from '../WithGap';

export const DeleteFileModal: FC = () => {
  const [showLoader, setShowLoader] = useState<boolean>(false);

  const texts = useTexts();
  const getTrackId = useGetTrackId();

  const [deleteDocumentFile, deleteDocumentFileRequest] =
    useDeleteDocumentFileMutation();

  const { documentFileToDelete, setDocumentFileToDelete } =
    useDocumentsArchiveContext();

  useEffect(() => setShowLoader(false), [documentFileToDelete?.id]);

  useKeyPress(() => {
    onCancelClick();
  }, ['Escape']);

  useKeyPress(
    () => {
      onDeleteClick();
    },
    ['Enter'],
    [documentFileToDelete?.id],
  );

  const onDeleteClick = async (): Promise<void> => {
    if (documentFileToDelete) {
      deleteDocumentFileRequest.reset();
      setShowLoader(true);
      await deleteDocumentFile(documentFileToDelete.id);
      setDocumentFileToDelete(null);
    }
  };

  const onCancelClick = (): void => {
    setDocumentFileToDelete(null);
  };

  useEffect(() => {
    if (documentFileToDelete) {
      deleteDocumentFileRequest.reset();
    }
  }, [documentFileToDelete?.id]);

  return (
    <Modal
      title={`${texts.deleteFileModal.confirmDelete} "${documentFileToDelete?.name}"?`}
      trigger={undefined}
      open={!!documentFileToDelete}
      onClose={onCancelClick}
      data-test="delete-file-modal"
      content={
        <>
          {texts.uploadFileModal.cannotBeUndone}
          {deleteDocumentFileRequest.isError && (
            <Alert
              variant="error"
              description={
                (deleteDocumentFileRequest.error as AxiosError)?.message
              }
            />
          )}
        </>
      }
      footer={
        <WithGap gap="16" direction="row">
          <Button
            onClick={onDeleteClick}
            disabled={showLoader}
            id="delete-file-button"
            key="delete-file-button"
            data-trackid={getTrackId(
              'delete:confirm-delete-modal',
              TrackingElement.Button,
            )}
            loading={showLoader}
          >
            {texts.iHub.delete.button}
          </Button>
          <Button
            variant="subtle"
            onClick={onCancelClick}
            disabled={showLoader}
            key="cancel-modal"
            data-trackid={getTrackId(
              'cancel:confirm-delete-modal',
              TrackingElement.Button,
            )}
          >
            {texts.admin.cancel}
          </Button>
        </WithGap>
      }
    ></Modal>
  );
};
